import { useCallback, useMemo, useState } from 'react';
import EasternGalileeMapPicker from '@/components/map/EasternGalileeMapPicker';
import type { ParcelIdentifyResult } from '@/lib/api/map';
import { useTranslation } from '@/i18n/useTranslation';
import type { WizardAnswers } from '@/context/WizardSessionContext';
import WizardField from '@/components/wizard/WizardField';
import WizardInput from '@/components/wizard/WizardInput';
import WizardSelect from '@/components/wizard/WizardSelect';
import wizardForm from '@/styles/wizard.module.css';
import styles from './VentureDetailsStep.module.css';

export type VentureDetailsErrors = {
  venture_type?: string;
  required_area_sqm?: string;
  timeline?: string;
  map_location?: string;
};

export interface VentureDetailsStepProps {
  answers: WizardAnswers;
  errors: VentureDetailsErrors;
  onChange: (patch: Partial<WizardAnswers>) => void;
  /** When false, parcel was chosen on the map screen (assessment-first journey). */
  showMapPicker?: boolean;
}

export default function VentureDetailsStep({
  answers,
  errors,
  onChange,
  showMapPicker = true,
}: VentureDetailsStepProps) {
  const t = useTranslation();
  const [parcelGeometry, setParcelGeometry] = useState<{
    type: string;
    coordinates: unknown;
  } | null>(null);
  const [selectionMode, setSelectionMode] = useState<'point' | 'polygon_drawn'>('point');
  const [identifyError, setIdentifyError] = useState<string | undefined>();

  const handleMapParcelIdentified = useCallback(
    (result: ParcelIdentifyResult) => {
      setIdentifyError(undefined);
      const geometry = result.geometry ?? null;
      setParcelGeometry(geometry);
      onChange({
        migrash: result.migrash,
        gush: result.gush,
        helka: result.helka,
        settlement_name: result.settlement_name,
        designation: result.designation,
        plan_primary: result.plan_primary,
        area_sqm_registered: result.area_sqm_registered,
        map_lat: result.lat,
        map_lng: result.lng,
        parcel_geometry: geometry ?? undefined,
        selection_mode: result.selection_mode ?? selectionMode,
        area_sqm_drawn: result.area_sqm_drawn,
      });
    },
    [onChange, selectionMode],
  );

  const ventureOptions = useMemo(
    () =>
      (['hotel', 'bnb', 'glamping', 'commercial_tourism', 'other'] as const).map((key) => ({
        value: key,
        label: t.wizard.ventureTypes[key],
      })),
    [t],
  );

  const timelineOptions = useMemo(
    () =>
      (['immediate', 'within_6_months', 'within_1_year', 'exploring'] as const).map((key) => ({
        value: key,
        label: t.wizard.timelines[key],
      })),
    [t],
  );

  const mapSelectedLabel = useMemo(() => {
    if (answers.map_lat == null || answers.map_lng == null) return undefined;
    const parts: string[] = [];
    if (answers.designation) {
      parts.push(answers.designation);
    }
    if (answers.settlement_name && !answers.designation) {
      parts.push(answers.settlement_name);
    }
    if (selectionMode !== 'polygon_drawn') {
      parts.push(`${answers.map_lat.toFixed(5)}, ${answers.map_lng.toFixed(5)}`);
    }
    return parts.join(' · ');
  }, [answers, selectionMode]);

  return (
    <section
      className={wizardForm.formFields}
      data-testid="venture-details-step"
      aria-label={t.wizard.steps.venture}
    >
      <WizardField
        label={t.wizard.fields.ventureType}
        required
        error={errors.venture_type}
        errorTestId="venture-type-error"
        testId="venture-type-dropdown"
      >
        <WizardSelect
          value={answers.venture_type ?? ''}
          options={ventureOptions}
          placeholder={t.wizard.fields.ventureTypePlaceholder}
          onChange={(value) => onChange({ venture_type: value || undefined })}
          hasError={Boolean(errors.venture_type)}
          aria-label={t.wizard.fields.ventureType}
          data-testid="venture-type-select"
        />
      </WizardField>

      <WizardField
        label={t.wizard.fields.requiredArea}
        required
        error={errors.required_area_sqm}
        testId="required-area-field"
      >
        <WizardInput
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          placeholder={t.wizard.fields.requiredAreaPlaceholder}
          value={answers.required_area_sqm != null ? String(answers.required_area_sqm) : ''}
          onChange={(value) => {
            const n = value.trim() === '' ? undefined : Number.parseFloat(value);
            onChange({
              required_area_sqm: Number.isFinite(n) ? n : undefined,
            });
          }}
          hasError={Boolean(errors.required_area_sqm)}
          data-testid="required-area-input"
          aria-label={t.wizard.fields.requiredArea}
        />
      </WizardField>

      <WizardField
        label={t.wizard.fields.timeline}
        required
        error={errors.timeline}
        errorTestId="timeline-error"
        testId="timeline-dropdown"
      >
        <WizardSelect
          value={answers.timeline ?? ''}
          options={timelineOptions}
          placeholder={t.wizard.fields.timelinePlaceholder}
          onChange={(value) => onChange({ timeline: value || undefined })}
          hasError={Boolean(errors.timeline)}
          aria-label={t.wizard.fields.timeline}
          data-testid="timeline-select"
        />
      </WizardField>

      {showMapPicker ? (
        <fieldset className={styles.parcelSection} data-testid="parcel-fields">
          <legend className={styles.parcelLegend}>{t.wizard.fields.parcelSection}</legend>
          <EasternGalileeMapPicker
            lat={answers.map_lat}
            lng={answers.map_lng}
            parcelGeometry={parcelGeometry}
            selectionMode={selectionMode}
            onSelectionModeChange={setSelectionMode}
            onParcelIdentified={handleMapParcelIdentified}
            onIdentifyError={(message) =>
              setIdentifyError(message || t.wizard.validation.mapIdentifyFailed)
            }
            hint={t.wizard.fields.mapHint}
            selectedLabel={mapSelectedLabel}
            error={errors.map_location}
            identifyError={identifyError}
            mapAriaLabel={t.wizard.fields.mapAriaLabel}
          />
        </fieldset>
      ) : null}
    </section>
  );
}
